import * as React from 'react';
import { useTranslation } from 'react-i18next';
import classnames from 'classnames';
import PhotosUploader from '@components/shared/Admin/PhotosUploader';
import type { DefaultPhotoUploadType } from '@components/shared/Admin/MediaUploader';
import { AdminListingPhoto, AdminLandlordPhoto, FirstPhotoOrientation } from '@root/types';
import Typography from '@styles/typography.module.less';
import AspectRatioSelector from '@components/shared/AspectRatioSelector';
import s from './MediaPhotosSection.module.less';
import PhotosTable from './PhotosTable';

// TODO: TRV-2844 & TRV-2845 Update these types to include Landlord and Building photos
type PhotoTypes = AdminListingPhoto | AdminLandlordPhoto;

type Props = {
  fetchRecord: () => void;
  photos: PhotoTypes[];
  googleDriveMediaDirectoryId?: string | null;
  googleDriveMediaDirectoryIdTwo?: string | null;
  onPhotoUpload: (uploadedFiles: DefaultPhotoUploadType[]) => PromiseLike<{ ok: boolean }>;
  onUpdatePhotoPosition: (photoIds: string[]) => void;
  onDeletePhoto: (photoId: string) => void;
  title: string;
  onFirstPhotoOrientationChange?: (newOrientation: string) => void;
  firstPhotoOrientation?: FirstPhotoOrientation;
  additionalColumn?: {
    title: React.ReactNode;
    render: (photo: any) => React.ReactNode;
  };
};

const MediaPhotosSection = ({
  title,
  photos,
  googleDriveMediaDirectoryId,
  googleDriveMediaDirectoryIdTwo,
  fetchRecord,
  onPhotoUpload,
  onUpdatePhotoPosition,
  onDeletePhoto,
  additionalColumn,
  onFirstPhotoOrientationChange,
  firstPhotoOrientation,
}: Props) => {
  const { t } = useTranslation('admin');

  const photoIdKey = photos.map((photo: PhotoTypes) => photo.id).join(',');

  return (
    <div data-testid="media-photos-section">
      <div className={classnames(s.mediaTableHeader)}>
        <div>
          <div className={classnames(Typography.headerMediumSemibold, s.photosTableTitle)}>
            {title}
          </div>
          {/* eslint-disable-next-line no-nested-ternary */}
          {googleDriveMediaDirectoryIdTwo ? (
            <a
              target="_blank noopener noreferrer"
              href={`https://drive.google.com/drive/folders/${googleDriveMediaDirectoryIdTwo}`}
              className="u-p-left-2x"
            >
              {t('media.viewMediaDirectoryTwo')}
            </a>
          ) : null}
          {googleDriveMediaDirectoryId ? (
            <a
              target="_blank noopener noreferrer"
              href={`https://drive.google.com/drive/folders/${googleDriveMediaDirectoryId}`}
              className="u-p-left-2x"
            >
              {t('media.viewMediaDirectoryOne')}
            </a>
          ) : null}
        </div>
        <div className={s.rightButtons}>
          {firstPhotoOrientation && onFirstPhotoOrientationChange ? (
            <AspectRatioSelector
              firstPhotoOrientation={firstPhotoOrientation}
              onChange={onFirstPhotoOrientationChange}
            />
          ) : null}
          <PhotosUploader
            marketplaceRequest={onPhotoUpload}
            multiple
            onSuccess={fetchRecord}
            buttonIcon="upload"
            buttonSize="small"
            buttonClass={s.photoUploadButton}
          />
        </div>
      </div>
      <div data-testid="v2-photos-table">
        {/* key is necessary to rerender when photo is deleted */}
        <PhotosTable
          key={photoIdKey}
          photos={photos}
          onUpdatePhotoPosition={onUpdatePhotoPosition}
          onDeletePhoto={onDeletePhoto}
          additionalColumn={additionalColumn}
        />
      </div>
    </div>
  );
};

export default MediaPhotosSection;
